import React from 'react';
import { useSelector } from 'react-redux';

import { getCurrency, getL10nMoney, getMarket } from '@travel/i18n';
import { Coupon } from '@travel/icons/ui';
import { MOBILE_SCREEN, useDeviceType } from '@travel/traveler-core/hooks';
import { cx } from '@travel/utils';

import { getIsGlobalCurrency } from 'store/markets/selectors';

import { Translate } from 'core/translate';
import type { CombinedIncentive } from 'ProviderRatePlan-Types';

import styles from './couponBadgeWithDiscount.module.scss';

type Props = {
  className?: string;
  /** Callback when the component has been clicked */
  onClick?: () => void;
  /** Combined Sales promotion incentive */
  combinedIncentive?: CombinedIncentive;
  /** Number of price after applied the coupon */
  priceAfterDiscount: number;
  /** Number of coupons acquired */
  noOfAppliedCoupons: number;
  /** Number of coupons applicable */
  noOfAvailableCoupons: number;
};

function CouponBadgeWithDiscount(props: Props) {
  const { combinedIncentive, noOfAppliedCoupons, noOfAvailableCoupons } = props;

  const isSP = useDeviceType() === MOBILE_SCREEN;

  const isGlobalCurrency = useSelector(getIsGlobalCurrency);
  const currency = useSelector(getCurrency);
  const market = useSelector(getMarket);

  if (combinedIncentive === undefined || (noOfAppliedCoupons === 0 && noOfAvailableCoupons === 0)) {
    return null;
  }

  const { totalDiscountIncentive, totalPointIncentive } = combinedIncentive;

  const discountAmount =
    totalDiscountIncentive && totalPointIncentive ? (
      <Translate
        id="Provider_Info.Room_List.Plan_Info.Coupons.Amount_Point"
        data={{
          promotion_amount_exact: getL10nMoney(
            market.marketCode,
            totalDiscountIncentive,
            currency,
            isGlobalCurrency,
          ),
          exact_points: totalPointIncentive,
        }}
      />
    ) : totalDiscountIncentive ? (
      <Translate
        id="Provider_Info.Room_List.Plan_Info.Coupons.Amount_Off"
        data={{
          promotion_amount_exact: getL10nMoney(
            market.marketCode,
            totalDiscountIncentive,
            currency,
            isGlobalCurrency,
          ),
        }}
      />
    ) : totalPointIncentive ? (
      <Translate
        id="Provider_Info.Room_List.Plan_Info.Coupons.Point"
        data={{
          exact_points: totalPointIncentive,
        }}
      />
    ) : null;

  /**
   * there are at least 1 available => x coupons available
   * all are acquired => x coupons applied
   */
  const couponMessage =
    noOfAvailableCoupons > 0 ? (
      <Translate
        id="Provider_Info.Room_List.Plan_Info.Coupons.Available"
        count={noOfAppliedCoupons + noOfAvailableCoupons}
        data={{ coupon_count: noOfAppliedCoupons + noOfAvailableCoupons }}
      />
    ) : (
      <Translate
        id="Provider_Info.Room_List.Plan_Info.Coupons.Applied"
        count={noOfAppliedCoupons}
        data={{ coupon_count: noOfAppliedCoupons + noOfAvailableCoupons }}
      />
    );

  return (
    <button
      className={cx(props.className, styles.wrapper)}
      onClick={props.onClick}
      data-testid="providerInfo-ratePlanList-couponBadgeWithDiscount-wrapper"
    >
      <div className={styles.text}>
        <div
          className={styles.subtitle}
          data-testid="providerInfo-ratePlanList-couponBadgeWithDiscount-message"
        >
          <Coupon size={16} color="appleRed" className={styles.couponIcon} />
          {couponMessage}
          {!isSP && <>&nbsp;{discountAmount}</>}
        </div>
        {isSP && discountAmount}
      </div>
    </button>
  );
}

export default CouponBadgeWithDiscount;
